function normalizePath(path) {
    return String(path || "")
        .replace(/\\/g, "/")
        .replace(/^\.\//, "")
        .replace(/^\/+/, "")
        .trim();
}

function classifyLogLevel(message) {
    const normalized = String(message || "").toLowerCase();
    if (!normalized) return "info";
    if (normalized.includes("warning")) return "warning";
    if (
        normalized.includes("error") ||
        normalized.includes("undefined control sequence") ||
        normalized.includes("emergency stop") ||
        normalized.startsWith("!")
    ) {
        return "error";
    }
    return "info";
}

function buildLogEntryId(index) {
    return `compile-log-entry-${index + 1}`;
}

export function normalizeCompileLogPath(path) {
    return normalizePath(path);
}

export function parseCompileLog(rawLog) {
    const text = String(rawLog || "").trim();
    if (!text) return [];

    const lines = text.split(/\r?\n/);
    const entries = [];

    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const fileLineMatch = line.match(
            /^([^:\s][^:]*?\.(?:tex|sty|cls|bib|bbl|aux|toc|out|ltx|txt)):(\d+):\s*(.*)$/i,
        );

        if (fileLineMatch) {
            const [, rawFile, rawLineNumber, rawMessage] = fileLineMatch;
            const details = [line];
            let cursor = index + 1;
            while (cursor < lines.length) {
                const nextLine = lines[cursor];
                if (
                    /^([^:\s][^:]*?\.(?:tex|sty|cls|bib|bbl|aux|toc|out|ltx|txt)):(\d+):\s*(.*)$/i.test(nextLine) ||
                    /^Output written on /.test(nextLine) ||
                    /^Transcript written on /.test(nextLine)
                ) {
                    break;
                }
                if (!nextLine.trim() && details.length > 1) {
                    break;
                }
                details.push(nextLine);
                cursor += 1;
            }
            index = cursor - 1;

            const message = rawMessage?.trim() || line.trim();
            entries.push({
                id: buildLogEntryId(entries.length),
                level: classifyLogLevel(message),
                file: normalizePath(rawFile),
                line: Number.parseInt(rawLineNumber, 10),
                message,
                raw: details.join("\n").trim(),
            });
            continue;
        }

        if (/^(LaTeX Warning|Package .* Warning|Warning:)/.test(line)) {
            entries.push({
                id: buildLogEntryId(entries.length),
                level: "warning",
                file: "",
                line: null,
                message: line.trim(),
                raw: line.trim(),
            });
            continue;
        }

        if (/^(Output written on |Transcript written on )/.test(line)) {
            entries.push({
                id: buildLogEntryId(entries.length),
                level: "info",
                file: "",
                line: null,
                message: line.trim(),
                raw: line.trim(),
            });
        }
    }

    return entries;
}

export function buildCompileLogAnnotationsByNodeId(entries, treeNodes) {
    const nodes = Array.isArray(treeNodes) ? treeNodes : [];
    const textNodes = nodes.filter((node) => node?.node_type === "text");
    const nodeIdByPath = new Map();

    textNodes.forEach((node) => {
        nodeIdByPath.set(normalizePath(node.path_cache || node.name || ""), node.id);
    });

    const annotationsByNodeId = {};
    const seenLineByNodeId = new Map();

    (Array.isArray(entries) ? entries : []).forEach((entry, index) => {
        if (!entry?.file || !entry?.line) return;

        const normalizedPath = normalizePath(entry.file);
        const directNodeId = nodeIdByPath.get(normalizedPath);
        const resolvedNodeId =
            directNodeId ||
            textNodes.find((node) => {
                const nodePath = normalizePath(node.path_cache || node.name || "");
                return nodePath.endsWith(`/${normalizedPath}`) || normalizedPath.endsWith(`/${nodePath}`);
            })?.id;

        if (!resolvedNodeId) return;

        if (!annotationsByNodeId[resolvedNodeId]) {
            annotationsByNodeId[resolvedNodeId] = [];
        }
        if (!seenLineByNodeId.has(resolvedNodeId)) {
            seenLineByNodeId.set(resolvedNodeId, new Set());
        }

        const seenLines = seenLineByNodeId.get(resolvedNodeId);
        const lineNumber = Number(entry.line);

        annotationsByNodeId[resolvedNodeId].push({
            id: entry.id || buildLogEntryId(index),
            entryIndex: annotationsByNodeId[resolvedNodeId].length,
            row: Math.max(0, lineNumber - 1),
            type: entry.level === "error" ? "error" : "warning",
            text: entry.message || "",
            source: "compile",
            ruleId: entry.level,
            firstOnLine: !seenLines.has(lineNumber),
        });

        seenLines.add(lineNumber);
    });

    return annotationsByNodeId;
}
